// Common shell every report renders inside.
//
// Provides:
//   • Title + subtitle + facility brand mark
//   • Date-range filter (from / to) shared across reports
//   • Action bar: Print · PDF · Word · Excel  (and the print-only hidden
//     `report-actions` class so those buttons disappear from the printout
//     itself, via the popup window's print CSS).
//   • Renders children inside a `report-printable` wrapper.  Reports pass
//     a `csv` prop describing the table they want exported to Excel;
//     everything else (PDF / Word / Print) just snapshots the printable
//     DOM node.
//
// Each report decides what to put inside.  This component just standardises
// the chrome and the export pipeline.

import React, { useRef } from "react";
import { exportAs } from "../../../utils/exporters";
import { useFacility } from "../../../context/FacilityContext";

export default function ReportShell({
  title,
  subtitle,
  // { from: "YYYY-MM-DD", to: "YYYY-MM-DD", onChange: (next)=>void }
  range,
  // For Excel export — the report passes its tabular data here.
  csv,             // { headers: [...], rows: [...] } or () => same
  // Anything to put in the action bar BEFORE the export buttons.
  toolbar,
  filename = "report",
  children,
}) {
  const printableRef = useRef(null);
  const { facility } = useFacility();

  const fmtRange = () => {
    if (!range?.from && !range?.to) return "All time";
    const f = range?.from ? fmtDay(range.from) : "the beginning";
    const t = range?.to   ? fmtDay(range.to)   : "today";
    return `${f} → ${t}`;
  };

  const handleExport = (format) => {
    const node = printableRef.current;
    if (!node) return;
    const csvData = typeof csv === "function" ? csv() : csv;
    // Build the body the PDF / Word file will contain — clone the
    // printable area plus a small header banner.
    const headerHtml = `
      <div style="margin-bottom:14px;">
        <div style="font-size:11px;letter-spacing:.18em;text-transform:uppercase;color:#8a6a30;">
          ${esc(facility?.name || "Hotel")}
        </div>
        <h1 style="margin:2px 0;">${esc(title)}</h1>
        <div class="report-meta">${esc(subtitle || "")}${subtitle ? " · " : ""}${esc(fmtRange())} · Generated ${esc(fmtNow())}</div>
      </div>
    `;
    exportAs(format, {
      node,
      htmlBody: headerHtml + (node?.innerHTML || ""),
      title: `${facility?.name || "Hotel"} — ${title}`,
      filename: `${slug(facility?.name || "hotel")}-${slug(title)}-${todayStamp()}`,
      csv: csvData,
    });
  };

  return (
    <div>
      {/* Action bar — never appears in the exported / printed output */}
      <div className="report-actions" style={{
        display: "flex", flexWrap: "wrap", gap: 10,
        alignItems: "center", marginBottom: 16,
      }}>
        {range && (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <label style={{ fontSize: 11.5, color: "#6b5142", letterSpacing: ".1em", textTransform: "uppercase", fontWeight: 500 }}>From</label>
            <input className="f-input" type="date" style={{ padding: "8px 10px", width: 160 }}
              value={range.from || ""}
              onChange={(e) => range.onChange?.({ ...range, from: e.target.value })}/>
            <label style={{ fontSize: 11.5, color: "#6b5142", letterSpacing: ".1em", textTransform: "uppercase", fontWeight: 500 }}>To</label>
            <input className="f-input" type="date" style={{ padding: "8px 10px", width: 160 }}
              value={range.to || ""}
              onChange={(e) => range.onChange?.({ ...range, to: e.target.value })}/>
            <button type="button" className="btn-outline" style={{ padding: "8px 12px", fontSize: 11 }}
              onClick={() => range.onChange?.({ from: "", to: "" })}>
              Clear
            </button>
          </div>
        )}

        {toolbar}

        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          <ExportButton icon="🖨️" label="Print"  onClick={() => handleExport("print")} />
          <ExportButton icon="📄" label="PDF"    onClick={() => handleExport("pdf")} />
          <ExportButton icon="📝" label="Word"   onClick={() => handleExport("word")} />
          <ExportButton icon="📊" label="Excel"  onClick={() => handleExport("excel")} disabled={!csv} />
        </div>
      </div>

      {/* Printable body */}
      <div ref={printableRef} className="report-printable">
        {/* Report header — only visible in the printed/exported copy via
            the popup window's stylesheet; on-screen we render the same
            content but the dashboard sidebar provides the title. */}
        <div style={{ marginBottom: 18 }}>
          <div style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 30, color: "#2c1f14", fontWeight: 500, marginBottom: 4,
          }}>
            {title}
          </div>
          <div style={{ fontSize: 13.5, color: "#6b5142" }}>
            {subtitle ? `${subtitle} · ` : ""}{fmtRange()} · Generated {fmtNow()}
          </div>
        </div>

        {children}
      </div>
    </div>
  );
}

function ExportButton({ icon, label, onClick, disabled }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      title={disabled ? "Not available for this report" : `Export as ${label}`}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        padding: "8px 12px", borderRadius: 7,
        background: disabled ? "rgba(180,140,80,.08)" : "transparent",
        border: "1.5px solid rgba(180,140,80,.3)",
        color: disabled ? "#b8a090" : "#3a2a1a",
        cursor: disabled ? "not-allowed" : "pointer",
        fontFamily: "'Jost', sans-serif",
        fontSize: 11.5, letterSpacing: ".1em", textTransform: "uppercase", fontWeight: 500,
        transition: "background .15s, border-color .15s",
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = "rgba(200,160,90,.08)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
      <span style={{ fontSize: 14 }}>{icon}</span> {label}
    </button>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────
function fmtDay(d) {
  const dt = new Date(`${d}T00:00:00`);
  return isNaN(dt) ? String(d) : dt.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}
function fmtNow() {
  return new Date().toLocaleString("en-GB", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}
function todayStamp() {
  return new Date().toISOString().slice(0, 10);
}
function slug(s) {
  return String(s || "report").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}
function esc(s) {
  return String(s || "").replace(/[&<>"']/g, c => ({ "&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;" }[c]));
}
